/* istanbul ignore file */
import React, { useState, useEffect } from 'react'
import { string, shape, func } from 'prop-types'
import { useQuery } from 'react-apollo-hooks'
import { FM } from '@divvy-web/i18n'
import {
  BANK_PLAID,
  BANK_ATRIUM,
  BANK_OPEN_CONNECT,
  BANK_WIDGET_STATE_CONNECT,
  BANK_WIDGET_STATE_FIX,
  BANK_WIDGET_STATE_CLOSED
} from '../../constants'
import MXWidget from './MXWidget'
import PlaidWidget from './PlaidWidget'
import GetBankMembersQuery from './GetBankMembersQuery.gql'
import DisconnectBankAccountMutation from './DisconnectBankAccountMutation.gql'
import ReconnectBankAccountMutation from './ReconnectBankAccountMutation.gql'

/************************************************************************************************
BankManager

If you need to work on this, you'll need to get viking running on your local machine by following
these instructions:

https://git-p1ap1.divvy.co/frontend/stardust#if-you-need-to-run-viking-alongside-for-testing-please

*************************************************************************************************/

const BankManagerData = ({ bankWidgetEnv, children, client, dataTestId, email, name, onConnected, onFailure = () => {}, onMembersLoaded }) => {
  // fetch the currently connected banks from viking
  const { data, error: loadingError, loading, refetch } = useQuery(GetBankMembersQuery, { client, fetchPolicy: 'no-cache' })
  // a connection error is an error where the bank failed to connect for some reason
  const [connectionError, setConnectionError] = useState(null)
  // which bank provider or we using? (Plaid, MX, etc)
  const [adapter, setAdapter] = useState(BANK_ATRIUM)
  // open, fix, closed. Used to render the widget or not
  const [widgetState, setWidgetState] = useState(BANK_WIDGET_STATE_CLOSED)
  // set when they click "reconnect" on a BankTile
  const [memberToFix, setMemberToFix] = useState()

  const membersData = data?.bankMembers?.edges
  useEffect(() => {
    if (onMembersLoaded && membersData !== undefined) {
      onMembersLoaded(membersData.map(({ node }) => node))
    }
  }, [membersData, onMembersLoaded])

  // This is once the widget has validated their login and they have selected accounts
  const onConnectedSuccess = () => {
    setWidgetState(BANK_WIDGET_STATE_CLOSED)
    // set adapter back to plaid because its the default
    setAdapter(BANK_ATRIUM)
    setConnectionError(null)
    // reload the bank accounts from viking
    refetch()
    // optional callback (used in sputnik)
    onConnected && onConnected()
  }

  const onToggleBankAccount = (bankAccount) => {
    client.mutate({
      mutation: bankAccount.connected ? DisconnectBankAccountMutation : ReconnectBankAccountMutation,
      variables: { input: { id: bankAccount.id } }
    }).then(() => {
      refetch()
    })
  }

  // if they close the widget before they finish connecting
  const onFailedToConnect = (error, adapter) => {
    setWidgetState(BANK_WIDGET_STATE_CLOSED)
    // whenever a widget is closed, we should set the adapter back to plaid. Only clicking the "try a different way"
    // button should open the alternate
    if (adapter !== BANK_ATRIUM) {
      setAdapter(BANK_ATRIUM)
      setConnectionError(null)
    } else {
      // The widget does a good job of communicating to the user and the user explicitly closes the widget,
      // so I don't think theres anything productive we can offer here…
      // one example error response was `null` another was:
      // {"display_message":"The provided credentials were not correct. Please try again.","error_code":"INVALID_CREDENTIALS","error_message":"the provided credentials were not correct","error_type":"ITEM_ERROR"}
      // just for posterity in case it becomes useful
      setConnectionError(error || <FM msg="We weren't able to connect. Try again just to be sure, or connect a different way." />)
    }

    // callback to let the consumer know the connection attempt failed
    onFailure()
  }

  // when the user clicks "Connect new bank", "reconect" or "try alternate"
  const open = (action, provider, member) => {
    setAdapter(provider)
    setWidgetState(action === BANK_OPEN_CONNECT ? BANK_WIDGET_STATE_CONNECT : BANK_WIDGET_STATE_FIX)
    setMemberToFix(member)
  }

  // props common to all widgets from any provider
  const widgetProps = {
    client,
    memberToFix,
    onConnectedSuccess,
    onFailedToConnect,
    widgetState
  }

  // clean data: remove `node` keyspace and drop members that dont have a `node` keyspace
  const members = data?.bankMembers?.edges?.map(({ node }) => node)

  useEffect(() => {
    if (onMembersLoaded && members !== undefined) {
      onMembersLoaded(members)
    }
  }, [members, onMembersLoaded])

  // to allow maximum customization of the ui, we pass all the needed functions and data to the children of this
  // component and let them do all the rendering themselves. The consumer can use components such as BankTile to
  // help with that rendering.
  let props = {
    errors: {
      connection: connectionError,
      loading: loadingError
    },
    loading,
    members: [],
    onToggleBankAccount,
    open
  }

  if (loading) {
    return children(props)
  }

  if (loadingError || !data?.bankMembers?.edges) {
    return children({
      ...props,
      errors: { loading: loadingError?.message || <FM msg='We were unable to get bank info. Please reach out to support.' /> }
    })
  }

  props = { ...props, members }

  const isWidgetOpen = [BANK_WIDGET_STATE_CONNECT, BANK_WIDGET_STATE_FIX].includes(widgetState)
  let widgetComponent = null
  if (isWidgetOpen) {
    if (adapter === BANK_PLAID) widgetComponent = <PlaidWidget {...widgetProps} email={email} env={bankWidgetEnv} name={name} />
    if (adapter === BANK_ATRIUM) widgetComponent = <MXWidget {...widgetProps} />
  }

  return (
    <>
      {children(props)}
      {widgetComponent}
    </>
  )
}

BankManagerData.propTypes = {
  bankWidgetEnv: shape({
    plaidEnv: string,
    plaidWebhook: string
  }),
  children: func,
  client: shape({
    query: func
  }),
  dataTestId: string,
  email: string,
  name: string,
  onConnected: func,
  onFailure: func,
  onMembersLoaded: func
}

export default BankManagerData
